import { useEffect, useRef, useState } from 'react';
import { Karti, type KartiMood } from './Karti';
import { useKartiMessage, type KartiContext, type KartiScreen } from '../../lib/karti';
import { cn } from '../../lib/utils';

/** Karti with a speech bubble. Tapping Karti makes it happy for a moment. */
export function KartiSay({
  mood,
  text,
  size = 64,
  className,
  compact = false,
}: {
  mood: KartiMood;
  text: string;
  size?: number;
  className?: string;
  compact?: boolean;
}) {
  const [tapped, setTapped] = useState(false);
  const timer = useRef<ReturnType<typeof setTimeout>>();
  useEffect(() => () => clearTimeout(timer.current), []);

  return (
    <div className={cn('flex items-center gap-2.5', className)}>
      <button
        type="button"
        aria-label="Karti"
        onClick={() => {
          setTapped(true);
          clearTimeout(timer.current);
          timer.current = setTimeout(() => setTapped(false), 1400);
        }}
        className="shrink-0 cursor-pointer"
      >
        <Karti size={size} mood={tapped ? 'happy' : mood} />
      </button>
      <div
        role="status"
        aria-live="polite"
        className={cn(
          'relative min-w-0 flex-1 rounded-list border-2.5 border-ink bg-cream font-bold leading-snug shadow-hard-sm',
          compact ? 'px-3 py-2 text-[13px]' : 'px-3.5 py-2.5 text-[15px]'
        )}
      >
        <span
          aria-hidden
          className="absolute -left-[7px] top-1/2 h-3 w-3 -translate-y-1/2 rotate-45 border-b-2.5 border-l-2.5 border-ink bg-cream"
        />
        {text}
      </div>
    </div>
  );
}

/** Always-present coach bubble: picks the line for a screen (stable per visit). Not dismissible. */
export function KartiCoachBubble({
  screen,
  ctx,
  size = 64,
  className,
  compact,
}: {
  screen: KartiScreen;
  ctx?: KartiContext;
  size?: number;
  className?: string;
  compact?: boolean;
}) {
  const msg = useKartiMessage(screen, ctx);
  return <KartiSay mood={msg.mood} text={msg.text} size={size} className={className} compact={compact} />;
}

/** Centered Karti with a line underneath, for loading, empty and not-found states. */
export function KartiState({
  screen,
  size = 96,
  mood,
  className,
}: {
  screen: 'loading' | 'empty' | 'notFound';
  size?: number;
  mood?: KartiMood;
  className?: string;
}) {
  const msg = useKartiMessage(screen);
  return (
    <div className={cn('flex flex-col items-center gap-3 text-center', className)}>
      <Karti size={size} mood={mood ?? msg.mood} />
      <p className="max-w-xs text-[15px] font-bold">{msg.text}</p>
    </div>
  );
}
